// ---------------------------------------------------------------------------
// Gate único do `resolveBookIdentity` — coalescing em voo + cache de sessão.
//
// ── O defeito que isto conserta (medido em prod, 06/08) ────────────────────
// Nos logs das 18:20:17 saíram TRÊS chamadas idênticas e simultâneas de
// `resolveBookIdentity` — mesmo livro, mesmo resultado (`isbn:9781781106587`,
// todas cache HIT) — e cada uma subiu a PRÓPRIA instância fria (3× "Starting
// new instance", ~4,1s de boot cada). O app pagou 3 cold starts para obter
// uma resposta que o servidor já tinha em cache.
//
// A causa é estrutural, não um call site distraído: `useCanonicalIdRedirect`
// tem 7 chamadas, mais `useBookDetailState` e `addBook`. Nenhuma delas sabia
// das outras, porque `resolveBookIdentityBackground` era fire-and-forget puro.
//
// ── Por que o gate mora aqui, e não em cada chamador ───────────────────────
// Mesma lição do BUG-81 (busca do onboarding): quem chama a fonte direto é o
// que faz duas cópias divergirem. O gate é o MÓDULO. `discoveryService`
// mantém `resolveBookIdentityBackground` como porta única e passa por aqui,
// então os 9 call sites ficam cobertos por construção — chamador novo nasce
// deduplicado sem lembrar de nada.
//
// ── Decisões assumidas ────────────────────────────────────────────────────
//  · **Dedup é por INPUT IDÊNTICO, não por livro.** Dois call sites podem
//    mandar entradas diferentes pro mesmo livro (um manda o `id` da rota,
//    outro o canonical já resolvido). Fundir entradas diferentes mudaria
//    comportamento — o backend pode legitimamente resolver coisas distintas.
//    Entrada igual → resultado igual é a única fusão que se prova correta.
//  · **Falha NÃO entra no cache.** O resolve também é o caminho de CURA do
//    catálogo (`needsResolve`: meta apagado pelo TTL do BUG-25, workId sem
//    lazy-backfill). Cachear falha travaria a cura até a aba ser recarregada.
//  · **TTL de 30 min, não a sessão inteira.** Aba aberta o dia todo não pode
//    bloquear a cura para sempre; o TTL do servidor é de 7d (weak) / 90d
//    (completo), então 30 min no client é folgado e ainda mata a rajada.
//  · **IDs não são lowercased.** `googleId` (volumeId) é case-sensitive.
//    Só `title`/`author` normalizam caixa e espaço — o backend normaliza
//    esses dois de qualquer jeito.
// ---------------------------------------------------------------------------

import type { ResolveBookIdentityInput } from "./discoveryService";

/** Janela em que uma resolução bem-sucedida suprime repetição do mesmo input. */
export const RESOLVE_CACHE_TTL_MS = 30 * 60 * 1000;

/** Teto de chaves lembradas — aba longa não vira vazamento de memória. */
export const RESOLVE_CACHE_MAX_ENTRIES = 500;

/** Promessas em andamento, por chave. Enquanto pende, ninguém chama de novo. */
const inFlight = new Map<string, Promise<void>>();

/** Chave → instante da conclusão bem-sucedida. */
const resolvedAt = new Map<string, number>();

function normalizeText(value: string | undefined): string {
  if (!value) return "";
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

function normalizeId(value: string | undefined): string {
  return value ? value.trim() : "";
}

/**
 * Chave estável e independente da ordem das propriedades do objeto. Serializa
 * os 6 campos em posição fixa — `JSON.stringify` sobre o input cru mudaria a
 * chave só porque um chamador montou o objeto em outra ordem.
 */
export function bookIdentityKey(input: ResolveBookIdentityInput): string {
  return [
    normalizeId(input.isbn13),
    normalizeId(input.isbn10),
    normalizeId(input.appleId),
    normalizeId(input.googleId),
    normalizeText(input.title),
    normalizeText(input.author),
  ].join(" ");
}

function rememberResolved(key: string, now: number): void {
  resolvedAt.set(key, now);
  if (resolvedAt.size <= RESOLVE_CACHE_MAX_ENTRIES) return;
  // Map preserva ordem de inserção: a primeira chave é a mais antiga.
  const oldest = resolvedAt.keys().next();
  if (!oldest.done) resolvedAt.delete(oldest.value);
}

function isFresh(key: string, now: number): boolean {
  const at = resolvedAt.get(key);
  if (at === undefined) return false;
  if (now - at < RESOLVE_CACHE_TTL_MS) return true;
  resolvedAt.delete(key);
  return false;
}

/**
 * Roda `invoke` no máximo uma vez por input dentro da janela de cache, e
 * exatamente uma vez enquanto houver chamada em voo.
 *
 * Devolve sempre uma promessa que RESOLVE (nunca rejeita) — o chamador é
 * fire-and-forget e um rejeitado solto viraria `unhandledrejection`.
 *
 * @param now injetável só para teste; produção usa o relógio real.
 */
export function runBookIdentityResolve(
  input: ResolveBookIdentityInput,
  invoke: () => Promise<unknown>,
  now: number = Date.now(),
): Promise<void> {
  const key = bookIdentityKey(input);

  if (isFresh(key, now)) return Promise.resolve();

  const pending = inFlight.get(key);
  if (pending) return pending;

  const run = invoke()
    .then(() => {
      rememberResolved(key, now);
    })
    .catch(() => {
      // Falha não entra no cache: a próxima tentativa precisa poder curar o
      // catálogo. Silenciosa porque o livro já está na estante — book_meta é
      // best-effort, como sempre foi neste caminho.
    })
    .finally(() => {
      inFlight.delete(key);
    });

  inFlight.set(key, run);
  return run;
}

/** Só para teste — zera o estado de módulo entre casos. */
export function __resetBookIdentityGate(): void {
  inFlight.clear();
  resolvedAt.clear();
}

/** Só para teste/diagnóstico — quantas chamadas estão em voo agora. */
export function __inFlightCount(): number {
  return inFlight.size;
}
